import { useMemo } from "react";

import {
  extractHeatEnergySum,
  getPast5YearsBlockNames,
  getPast12MonthsBlockNames,
  getPrevious12MonthsBlockNames,
} from "@core/utils/energyUtils";

import type { EnergyConsumptionSummary, EnergySeries } from "../types/energy.type";
import { useInfoBlocks } from "./propai.hooks";

export const useEnergyConsumptionSummary = (substationId: string, networkUid?: string) => {
  const last12BlockNames = getPast12MonthsBlockNames();
  const prev12BlockNames = getPrevious12MonthsBlockNames();
  const last5YearsBlockNames = getPast5YearsBlockNames();

  const { data: last12Raw, isLoading: last12Loading } = useInfoBlocks(
    substationId,
    last12BlockNames,
    { networkUid }
  );
  const { data: prev12Raw, isLoading: prev12Loading } = useInfoBlocks(
    substationId,
    prev12BlockNames,
    { networkUid }
  );
  const { data: last5YearsRaw, isLoading: last5YearsLoading } = useInfoBlocks(
    substationId,
    last5YearsBlockNames,
    { networkUid }
  );

  const isLoading = last12Loading || prev12Loading || last5YearsLoading;

  const data: EnergyConsumptionSummary = useMemo(() => {
    const last12Items = extractHeatEnergySum(last12Raw?.collection ?? []);
    const prev12Items = extractHeatEnergySum(prev12Raw?.collection ?? []);
    const last5Items = extractHeatEnergySum(last5YearsRaw?.collection ?? []);

    const sumItems = (items: any[]) =>
      items.reduce(
        (total, item) =>
          typeof item.heat_energy_sum === "number" ? total + item.heat_energy_sum : total,
        0
      );

    const monthlySeries: EnergySeries = { series: [], categories: [] };
    last12Items.forEach((item: any) => {
      monthlySeries.series.push(item.heat_energy_sum);
      monthlySeries.categories.push(item.month_name);
    });

    const valueByYear = new Map<string, number>();
    last5Items.forEach((item: any) => {
      if (!item?.block_name) return;
      const parts = item.block_name.split("_");
      const year = parts[1] || parts[0];
      if (year) {
        valueByYear.set(year, typeof item.heat_energy_sum === "number" ? item.heat_energy_sum : 0);
      }
    });

    const yearlySeries: EnergySeries = { series: [], categories: [] };
    last5YearsBlockNames.forEach((blockName) => {
      const parts = blockName.split("_");
      const year = parts[1] || parts[0];
      yearlySeries.categories.push(year);
      yearlySeries.series.push(valueByYear.get(year) ?? 0);
    });

    return {
      totalLast12Months: sumItems(last12Items),
      totalPrevious12Months: sumItems(prev12Items),
      monthlySeries,
      yearlySeries,
    };
  }, [last12Raw, prev12Raw, last5YearsRaw, last5YearsBlockNames]);

  return {
    data,
    isLoading,
    isMonthlyLoading: last12Loading,
    isYearlyLoading: last5YearsLoading,
    isEmpty: !isLoading && data.totalLast12Months === 0 && data.monthlySeries.series.length === 0,
  };
};
